/* eslint-disable brace-style */
import type { RicosEditorProps } from './../../ricos-common';
import type {
  IRicosInstantiablePluginMenuToolbar,
  IRicosInstantiableToolbar,
  IRicosToolbars,
  KeyboardShortcut,
  PublisherProvider,
  RicosServices,
  TextButtons,
  ToolbarSettingsFunctions,
  ToolbarType,
} from './../../ricos-types';
import { mergeToolbarSettings, TOOLBARS } from './../../editor-common';
import { TEXT_BUTTON_LINK } from './../../plugin-link/data-hooks';

import { TOOLBAR_VISIBLE } from '../components/Toolbar';
import { getDefaultToolbarSettings } from '../default-toolbar-settings';
import { getShouldCreate, getToolbarSettings } from '../toolbar-utils';
import type { Topics } from '../topics';
import { topics } from '../topics';
import type { ToolbarServices } from '../types';
import { EmptyToolbar } from './empty/empty-toolbar';
import { FooterToolbar } from './footer/footer-toolbar';
import { FLOATING_FORMATTING_TOOLBAR } from './formatting/data-hooks';
import { FormattingToolbar } from './formatting/formatting-toolbar';
import { MegaToolbar } from './mega/mega-toolbar';
import { MobileToolbar } from './mobile/mobile-toolbar';
import { FLOATING_PLUGIN_TOOLBAR } from './plugin/data-hooks';
import { PluginToolbar } from './plugin/plugin-toolbar';
import { PluginMenuToolbar } from './plugin-menu/plugin-menu-toolbar';
import { RicosToolbar } from './ricos-toolbar';
import { STATIC_TOOLBAR_DATA_HOOK } from './static/data-hooks';
import { StaticToolbar } from './static/static-toolbar';

export class RicosToolbars implements IRicosToolbars {
  topicsToPublish = topics;

  publishers!: PublisherProvider<Topics>;

  private staticToolbar: IRicosInstantiableToolbar<ToolbarType.STATIC> = new EmptyToolbar();

  private formattingToolbar: IRicosInstantiableToolbar<ToolbarType.INLINE> = new EmptyToolbar();

  private mobileToolbar: IRicosInstantiableToolbar<ToolbarType.MOBILE> = new EmptyToolbar();

  private externalToolbar: IRicosInstantiableToolbar<ToolbarType.EXTERNAL> = new EmptyToolbar();

  private footerToolbar: IRicosInstantiableToolbar<ToolbarType.FOOTER> = new EmptyToolbar();

  private pluginToolbar: IRicosInstantiableToolbar<ToolbarType.PLUGIN> = new EmptyToolbar();

  private pluginMenuToolbar: IRicosInstantiablePluginMenuToolbar<ToolbarType.SIDE> =
    new EmptyToolbar();

  private megaToolbar: IRicosInstantiableToolbar<ToolbarType.MEGA> = new EmptyToolbar();

  private isMobile: boolean;

  constructor(isMobile: boolean) {
    this.isMobile = isMobile;
  }

  initToolbarSettings(
    toolbarSettings: RicosEditorProps['toolbarSettings'],
    plugins: RicosServices['plugins']
  ) {
    const textButtons: TextButtons = {
      mobile: [],
      desktop: [],
    };

    const pluginButtons = plugins
      .getAddButtons()
      .asArray()
      .map(b => b.toToolbarButtonSettings());
    const defaultSettings = getDefaultToolbarSettings({
      pluginButtons,
    });
    const customSettings =
      toolbarSettings?.getToolbarSettings?.({
        textButtons,
      }) || [];

    return mergeToolbarSettings({ defaultSettings, customSettings });
  }

  initToolbars(toolbarSettings: RicosEditorProps['toolbarSettings'], services: ToolbarServices) {
    const finalToolbarSettings = this.initToolbarSettings(toolbarSettings, services.plugins);

    this.externalToolbar = new RicosToolbar(
      this.isMobile,
      services,
      TOOLBARS.EXTERNAL,
      this.publishers,
      getToolbarSettings(finalToolbarSettings, TOOLBARS.EXTERNAL)
    );

    this.staticToolbar = new StaticToolbar(
      this.isMobile,
      services,
      this.publishers,
      getToolbarSettings(finalToolbarSettings, TOOLBARS.STATIC),
      !!toolbarSettings?.useStaticTextToolbar &&
        this.shouldCreate(TOOLBARS.STATIC, finalToolbarSettings)
    );

    this.formattingToolbar = new FormattingToolbar(
      this.isMobile,
      services,
      this.publishers,
      getToolbarSettings(finalToolbarSettings, TOOLBARS.INLINE),
      !this.isMobile &&
        !toolbarSettings?.useStaticTextToolbar &&
        this.shouldCreate(TOOLBARS.INLINE, finalToolbarSettings)
    );

    this.mobileToolbar = new MobileToolbar(
      this.isMobile,
      services,
      this.publishers,
      getToolbarSettings(finalToolbarSettings, TOOLBARS.MOBILE),
      this.isMobile && this.shouldCreate(TOOLBARS.MOBILE, finalToolbarSettings)
    );

    this.footerToolbar = new FooterToolbar(
      this.isMobile,
      services,
      this.publishers,
      getToolbarSettings(finalToolbarSettings, TOOLBARS.FOOTER),
      !this.isMobile && this.shouldCreate(TOOLBARS.FOOTER, finalToolbarSettings)
    );

    this.pluginToolbar = new PluginToolbar(
      this.isMobile,
      services,
      this.publishers,
      getToolbarSettings(finalToolbarSettings, TOOLBARS.PLUGIN),
      this.shouldCreate(TOOLBARS.PLUGIN, finalToolbarSettings)
    );

    this.pluginMenuToolbar = new PluginMenuToolbar(
      this.isMobile,
      services,
      this.publishers,
      getToolbarSettings(finalToolbarSettings, TOOLBARS.SIDE),
      this.shouldCreate(TOOLBARS.SIDE, finalToolbarSettings)
    );

    this.megaToolbar = new MegaToolbar(
      this.isMobile,
      services,
      {
        formatting: this.formattingToolbar,
        plugin: this.pluginToolbar,
        footer: this.footerToolbar,
        mobile: this.mobileToolbar,
        pluginMenu: this.pluginMenuToolbar,
      },
      this.publishers
    );
  }

  shouldCreate(toolbarType: TOOLBARS, toolbarSettings: ToolbarSettingsFunctions[]) {
    return getShouldCreate(
      this.isMobile,
      getToolbarSettings(toolbarSettings, toolbarType)?.shouldCreate
    );
  }

  get static() {
    return this.staticToolbar;
  }

  get formatting() {
    return this.formattingToolbar;
  }

  get mobile() {
    return this.mobileToolbar;
  }

  get external() {
    return this.externalToolbar;
  }

  get footer() {
    return this.footerToolbar;
  }

  get plugin() {
    return this.pluginToolbar;
  }

  get pluginMenu() {
    return this.pluginMenuToolbar;
  }

  get mega() {
    return this.megaToolbar;
  }

  byType(type: ToolbarType) {
    const toolbars = [
      this.pluginMenu,
      this.plugin,
      this.footer,
      this.external,
      this.mobile,
      this.formatting,
      this.static,
      this.megaToolbar,
    ];
    return toolbars.filter(toolbar => toolbar?.type === type)[0];
  }

  getShortcuts(): KeyboardShortcut[] {
    const focusShortcut: KeyboardShortcut = {
      name: 'EnterToolbar',
      description: 'ShortcutsSheet_ToolbarNavigation_EnterToolbar',
      group: 'ShortcutsSheet_Section_ToolbarNavigation',
      keys: { macOs: 'Ctrl+T', windows: 'Alt+T' },
      command() {
        const firstFloatingFormattingToolbarButton = document.querySelector(
          `[data-hook~=${FLOATING_FORMATTING_TOOLBAR}] [data-hook~=${TOOLBAR_VISIBLE}] Button`
        ) as HTMLElement;
        firstFloatingFormattingToolbarButton?.focus();

        const firstStaticFormattingToolbarButton = document.querySelector(
          `[data-hook~=${STATIC_TOOLBAR_DATA_HOOK}] [data-hook~=${TOOLBAR_VISIBLE}] Button`
        ) as HTMLElement;
        firstStaticFormattingToolbarButton?.focus();

        const firstLinkToolbarButton = document.querySelector(
          `[data-hook=${TEXT_BUTTON_LINK}] [data-hook~=${TOOLBAR_VISIBLE}] a`
        ) as HTMLElement;
        firstLinkToolbarButton?.focus();

        const firstPluginToolbarButton = document.querySelector(
          `[data-hook~=${FLOATING_PLUGIN_TOOLBAR}] [data-hook~=${TOOLBAR_VISIBLE}] button`
        ) as HTMLElement;
        firstPluginToolbarButton?.focus();
      },
      enabled: true,
    };

    return [focusShortcut];
  }

  getVisibleToolbars() {
    return [
      this.formatting,
      this.mobile,
      this.external,
      this.footer,
      this.plugin,
      this.pluginMenu,
      this.static,
    ].filter(toolbar => toolbar.isVisible() && toolbar);
  }
}
